import { useCallback, useEffect } from 'react'

import { useLocalStorage } from 'usehooks-ts'

import type {
  Event,
  ExternalSearchResult,
  Member,
  Post,
  Space,
} from '@tribeplatform/gql-client/types'

import {
  ExtendedSmartSearchEntity,
  ExtendedSmartSearchEntityType,
} from '../../../SmartSearch.types.js'
import { getSearchItemURL } from '../../../utils/url-helpers.js'
import {
  EXCLUDED_SEARCH_HISTORY_ENTITY_TYPES,
  MAX_DAYS_TO_KEEP,
  MAX_HISTORY_ITEMS,
  STORAGE_KEY,
} from '../searchHistory.constants.js'
import { type HistoryItem } from '../SearchHistory.types.js'

export const useSearchHistory = () => {
  const [history, setHistory] = useLocalStorage<HistoryItem[]>(STORAGE_KEY, [])

  // Clean up history on mount:
  // - Remove invalid entries
  // - Remove expired entries
  // - Limit to maximum items
  // - Remove excluded types
  useEffect(() => {
    const now = Date.now()
    const maxAge = MAX_DAYS_TO_KEEP * 24 * 60 * 60 * 1000

    const validHistory = (history || [])
      .filter((item: HistoryItem) => {
        // Validate data structure
        const isValid = item?.id && item?.type && item?.timestamp && item?.data
        // Check if item is within retention period
        const isRecent = now - item.timestamp < maxAge
        // Check if item type is not excluded
        const isAllowedType = !EXCLUDED_SEARCH_HISTORY_ENTITY_TYPES.includes(
          item.type,
        )
        return isValid && isRecent && isAllowedType
      })
      .slice(0, MAX_HISTORY_ITEMS)

    setHistory(validHistory)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submitItem = useCallback(
    (
      item: ExtendedSmartSearchEntity,
      entityType: ExtendedSmartSearchEntityType,
    ) => {
      if (EXCLUDED_SEARCH_HISTORY_ENTITY_TYPES.includes(entityType)) {
        return
      }

      // Skip entities with empty required fields
      switch (entityType) {
        case ExtendedSmartSearchEntityType.MEMBERS:
          if (!(item as Member).name?.trim()) {
            return
          }
          break
        case ExtendedSmartSearchEntityType.OFFICIAL_POSTS:
        case ExtendedSmartSearchEntityType.POSTS:
          if (!(item as Post).title?.trim()) {
            return
          }
          break
        case ExtendedSmartSearchEntityType.EVENTS:
          if (!(item as Event).title?.trim()) {
            return
          }
          break
        case ExtendedSmartSearchEntityType.SPACES:
          if (!(item as Space).name?.trim()) {
            return
          }
          break
        case ExtendedSmartSearchEntityType.EXTERNALS:
          if (!(item as ExternalSearchResult).title?.trim()) {
            return
          }
          break
        case ExtendedSmartSearchEntityType.HISTORY:
          return
        default: {
          const _exhaustiveCheck: never = entityType
          throw new Error(`Unhandled entity type: ${_exhaustiveCheck}`)
        }
      }

      let historyItem: Omit<HistoryItem, 'timestamp'>

      // Extract and store fields based on entity type
      switch (entityType) {
        case ExtendedSmartSearchEntityType.MEMBERS:
          historyItem = {
            id: item.id,
            type: ExtendedSmartSearchEntityType.MEMBERS,
            data: {
              title: (item as Member).name,
              description: (item as Member).role?.name,
              url: getSearchItemURL({
                entityType: ExtendedSmartSearchEntityType.MEMBERS,
                item: item as ExtendedSmartSearchEntity,
              }),
            },
          }
          break
        case ExtendedSmartSearchEntityType.OFFICIAL_POSTS:
        case ExtendedSmartSearchEntityType.POSTS:
          historyItem = {
            id: item.id,
            type: ExtendedSmartSearchEntityType.POSTS,
            data: {
              title: (item as Post).title,
              description:
                (item as Post).contentSummary?.summary ||
                (item as Post).description,
              url: getSearchItemURL({
                entityType: ExtendedSmartSearchEntityType.POSTS,
                item: item as ExtendedSmartSearchEntity,
              }),
            },
          }
          break
        case ExtendedSmartSearchEntityType.EVENTS:
          historyItem = {
            id: item.id,
            type: ExtendedSmartSearchEntityType.EVENTS,
            data: {
              title: (item as Event).title,
              description:
                (item as Event).shortContent || (item as Event).content,
              url: getSearchItemURL({
                entityType: ExtendedSmartSearchEntityType.EVENTS,
                item: item as ExtendedSmartSearchEntity,
              }),
            },
          }
          break
        case ExtendedSmartSearchEntityType.SPACES:
          historyItem = {
            id: item.id,
            type: ExtendedSmartSearchEntityType.SPACES,
            data: {
              title: (item as Space).name,
              description: (item as Space).description,
              url: getSearchItemURL({
                entityType: ExtendedSmartSearchEntityType.SPACES,
                item: item as ExtendedSmartSearchEntity,
              }),
            },
          }
          break
        case ExtendedSmartSearchEntityType.EXTERNALS:
          historyItem = {
            id: item.id,
            type: ExtendedSmartSearchEntityType.EXTERNALS,
            data: {
              title: (item as ExternalSearchResult).title,
              description: (item as ExternalSearchResult).description,
              url: (item as ExternalSearchResult).url,
            },
          }
          break
        default: {
          const _exhaustiveCheck: never = entityType
          throw new Error(`Unhandled entity type: ${_exhaustiveCheck}`)
        }
      }

      setHistory(currentHistory => {
        // Remove any existing entry with the same ID
        const filtered = (currentHistory || []).filter(h => h.id !== item.id)
        // put the new item at the beginning of the array
        return [{ ...historyItem, timestamp: Date.now() }, ...filtered].slice(
          0,
          MAX_HISTORY_ITEMS,
        ) as HistoryItem[]
      })
    },
    [setHistory],
  )

  const removeItem = useCallback(
    (item: HistoryItem) => {
      setHistory(currentHistory => {
        return (currentHistory || []).filter(h => h.id !== item.id)
      })
    },
    [setHistory],
  )

  return {
    items: history || [],
    submitItem,
    removeItem,
  }
}
